// src/components/Payments.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, Check, Zap, Star, Users, Shield, CreditCard, ArrowRight, Clock, UserCheck } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Payments = ({ testMode = false, userId = null, isUpgrade = false, isRestart = false, isExpired = false, onClose }) => {
  const { currentUser, currentOrg, isOrgAdmin } = useAuth();
  const t = useTranslation();
  const [activeTab, setActiveTab] = useState('monthly');
  const [loading, setLoading] = useState(false);

  // Use organization email if available, fallback to user email
  const billingEmail = currentOrg?.domain ? `billing@${currentOrg.domain}` : currentUser?.email;

  // If user is not an org admin, show contact admin message
  if (!isOrgAdmin) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl mx-auto overflow-hidden relative">
        {/* Header with Close Button */}
        <div className="relative px-4 md:px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-800 dark:to-indigo-800">
          {/* Only show close button if not expired */}
          {onClose && !isExpired && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          )}
          
          <div className="text-center text-white">
            <div className="flex items-center justify-center mb-2">
              {isExpired && <Clock className="w-6 h-6 mr-2" />}
              <h2 className="text-2xl md:text-3xl font-bold">
                {isExpired
                  ? t('trial.expired.title', 'Your Trial Has Expired')
                  : isRestart 
                    ? t('pricing.restart_subscription', 'Restart Your Subscription')
                    : isUpgrade 
                      ? t('pricing.upgrade_plan', 'Upgrade Your Plan')
                      : t('pricing.choose_plan', 'Choose Your Plan')
                }
              </h2>
            </div>
            <p className="text-blue-100 dark:text-blue-200">
              {isExpired
                ? t('trial.expired.message', 'Your free trial has ended. Please contact your organization admin to continue.')
                : t('pricing.subtitle', 'Contact your organization admin for billing management')
              }
            </p>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {/* Contact Admin Message */}
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-6">
              <UserCheck className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('billing.adminRequired', 'Organization Admin Access Required')}
            </h3>
            
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
              {t('profile.contactAdmin', 'Contact your organization admin to manage billing and subscriptions.')}
            </p>

            {currentOrg?.name && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">{t('common.organization', 'Organization')}:</span> {currentOrg.name}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const plans = {
    monthly: [
      {
        name: t('pricing.starter', 'Starter'),
        price: '€99',
        period: t('pricing.monthly', '/month'),
        features: [
          t('pricing.searches_1000', 'Up to 1,000 searches per month'),
          t('pricing.team_members_3', '3 team members'),
          t('pricing.active_monitoring', 'Active Monitoring'),
          t('pricing.case_management', 'Case Management')
        ],
        buttonText: t('pricing.get_started', 'Get Started'),
        popular: false,
        priceId: 'price_1RkhZOBgOAyRPxg3SYEfGkd9'
      },
      {
        name: t('pricing.professional', 'Professional'),
        price: '€199',
        period: t('pricing.monthly', '/month'),
        features: [
          t('pricing.searches_10000', 'Up to 10,000 searches per month'),
          t('pricing.team_members_10', '10 team members'),
          t('pricing.batch_screening', 'Batch screening'),
          t('pricing.rest_api_access', 'REST API Access'),
          t('pricing.active_monitoring', 'Active Monitoring'),
          t('pricing.case_management', 'Case Management')
        ],
        buttonText: t('pricing.upgrade_now', 'Upgrade Now'),
        popular: true,
        priceId: 'price_1RkhdGBgOAyRPxg33Eqs6UhV'
      },
      {
        name: t('pricing.enterprise', 'Enterprise'),
        price: t('pricing.custom', 'Custom'),
        period: '',
        features: [
          t('pricing.unlimited_searches', 'Unlimited searches'),
          t('pricing.dedicated_support', 'Dedicated account manager'),
          t('pricing.custom_integrations', 'Custom integrations'),
          t('pricing.white_label', 'White-label solution'),
          t('pricing.sla', 'SLA guarantee'),
          t('pricing.on_premise', 'On-premise deployment option')
        ],
        buttonText: t('pricing.contact_sales', 'Contact Sales'),
        popular: false,
        isCustom: true
      }
    ],
    yearly: [
      {
        name: t('pricing.starter', 'Starter'),
        price: '€79',
        period: t('pricing.monthly', '/month'),
        yearlyPrice: '€948',
        yearlyPeriod: t('pricing.billed_annually', 'billed annually'),
        originalPrice: '€1,188',
        features: [
          t('pricing.searches_1000', 'Up to 1,000 searches per month'),
          t('pricing.team_members_3', '3 team members'),
          t('pricing.active_monitoring', 'Active Monitoring'),
          t('pricing.case_management', 'Case Management')
        ],
        buttonText: t('pricing.get_started', 'Get Started'),
        popular: false,
        priceId: 'price_1Rkha4BgOAyRPxg30O7m5arl'
      },
      {
        name: t('pricing.professional', 'Professional'),
        price: '€159',
        period: t('pricing.monthly', '/month'),
        yearlyPrice: '€1,908',
        yearlyPeriod: t('pricing.billed_annually', 'billed annually'),
        originalPrice: '€2,388',
        features: [
          t('pricing.searches_10000', 'Up to 10,000 searches per month'),
          t('pricing.team_members_10', '10 team members'),
          t('pricing.batch_screening', 'Batch screening'),
          t('pricing.rest_api_access', 'REST API Access'),
          t('pricing.active_monitoring', 'Active Monitoring'),
          t('pricing.case_management', 'Case Management')
        ],
        buttonText: t('pricing.upgrade_now', 'Upgrade Now'),
        popular: true,
        priceId: 'price_1RkheeBgOAyRPxg3exY16XHz'
      },
      {
        name: t('pricing.enterprise', 'Enterprise'),
        price: t('pricing.custom', 'Custom'),
        period: '',
        features: [
          t('pricing.unlimited_searches', 'Unlimited searches'),
          t('pricing.dedicated_support', 'Dedicated account manager'),
          t('pricing.custom_integrations', 'Custom integrations'),
          t('pricing.white_label', 'White-label solution'),
          t('pricing.sla', 'SLA guarantee'),
          t('pricing.on_premise', 'On-premise deployment option')
        ],
        buttonText: t('pricing.contact_sales', 'Contact Sales'),
        popular: false,
        isCustom: true
      }
    ]
  };

  const handlePlanSelection = async (plan) => {
    if (plan.isCustom) {
      window.location.href = 'mailto:sales@sanctionscreener.eu?subject=Enterprise Plan Inquiry';
      return;
    }
    
    // Check if user is authenticated
    if (!currentUser) {
      console.error('User not authenticated');
      alert('Please log in to continue with payment');
      setLoading(false);
      return;
    }
    
    // Check if user has an organization
    if (!currentOrg?.id) {
      console.error('User not part of an organization');
      alert('You must be part of an organization to subscribe');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    // Open window immediately to preserve user gesture with loading page
    const loadingHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Loading Payment...</title>
        <style>
          body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            color: white;
          }
          .loading-container {
            text-align: center;
            max-width: 400px;
            padding: 2rem;
          }
          .spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255,255,255,0.3);
            border-radius: 50%;
            border-top-color: white;
            animation: spin 1s ease-in-out infinite;
            margin: 0 auto 1.5rem;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          h1 {
            font-size: 1.5rem;
            margin-bottom: 0.5rem;
            font-weight: 600;
          }
          p {
            opacity: 0.9;
            line-height: 1.5;
            margin: 0;
          }
          .logo {
            width: 60px;
            height: 60px;
            background: white;
            border-radius: 12px;
            margin: 0 auto 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #667eea;
          }
          .card-icon {
            width: 32px;
            height: 20px;
            background: #667eea;
            border-radius: 4px;
            position: relative;
          }
          .card-icon::before {
            content: '';
            position: absolute;
            top: 6px;
            left: 4px;
            right: 4px;
            height: 2px;
            background: white;
          }
          .card-icon::after {
            content: '';
            position: absolute;
            bottom: 4px;
            left: 4px;
            width: 12px;
            height: 2px;
            background: white;
            border-radius: 1px;
          }
        </style>
      </head>
      <body>
        <div class="loading-container">
          <div class="logo">
            <div class="card-icon"></div>
          </div>
          <div class="spinner"></div>
          <h1>Setting up your payment...</h1>
          <p>Please wait while we redirect you to our secure payment processor.</p>
        </div>
      </body>
      </html>
    `;
    
    const checkoutWindow = window.open('', '_blank');
    checkoutWindow.document.write(loadingHTML);
    checkoutWindow.document.close();
    
    try {
      console.log('Creating checkout session for user:', currentUser.uid, 'org:', currentOrg.id);
      
      // Get authentication token
      const token = await currentUser.getIdToken(true);
      
      // Cloud function URL - using the new createCheckoutSessionSimple function
      const region = 'us-central1'; // or your specific region
      const projectId = 'sanctions-screener-20d59'; // your Firebase project ID
      const functionUrl = `https://${region}-${projectId}.cloudfunctions.net/createCheckoutSessionSimple`;
      
      // Make direct fetch call to the function (same pattern as Profile.js)
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          origin: window.location.origin
        })
      });
      
      // Handle response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', response.status, errorData);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('Checkout session result:', data);
      
      if (data.url) {
        // Navigate the already-opened window to the checkout URL
        checkoutWindow.location.href = data.url;
        setLoading(false);
      } else {
        // Close the window if we failed to get a URL
        checkoutWindow.close();
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      
      // Close the window if there was an error
      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.close();
      }
      
      // Show user-friendly error message
      if (error.message.includes('401')) {
        alert('Please log in to continue with payment');
      } else if (error.message.includes('400')) {
        alert('You must be part of an organization to subscribe');
      } else {
        alert('Error creating payment session. Please try again.');
      }
      
      setLoading(false);
    }
  };

  const savings = {
    starter: Math.round(((1188 - 948) / 1188) * 100),
    professional: Math.round(((2388 - 1908) / 2388) * 100)
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl mx-auto overflow-hidden relative">
      {/* Header with Close Button */}
      <div className="relative px-4 md:px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-800 dark:to-indigo-800">
        {/* Only show close button if not expired */}
        {onClose && !isExpired && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}
        
        <div className="text-center text-white">
          <div className="flex items-center justify-center mb-2">
            {isExpired && <Clock className="w-6 h-6 mr-2" />}
            <h2 className="text-2xl md:text-3xl font-bold">
              {isExpired
                ? t('trial.expired.title', 'Your Trial Has Expired')
                : isRestart 
                  ? t('pricing.restart_subscription', 'Restart Your Subscription')
                  : isUpgrade 
                    ? t('pricing.upgrade_plan', 'Upgrade Your Plan')
                    : t('pricing.choose_plan', 'Choose Your Plan')
              }
            </h2>
          </div>
          <p className="text-blue-100 dark:text-blue-200">
            {isExpired
              ? t('trial.expired.message', 'Your free trial has ended. Choose a plan below to continue using SanctionsScreener.')
              : t('pricing.subtitle', 'Unlock the full power of sanctions screening')
            }
          </p>
        </div>
      </div>

      <div className="p-4 md:p-6">
        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === 'monthly'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t('pricing.monthly', 'Monthly')}
            </button>
            <button
              onClick={() => setActiveTab('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'yearly'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t('pricing.yearly', 'Yearly')}
              <span className="absolute -top-3 -right-4 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                {t('pricing.save', 'Save')} 20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {plans[activeTab].map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-xl border-2 p-6 bg-white dark:bg-gray-700 transition-all hover:shadow-lg ${
                plan.popular 
                  ? 'border-blue-500 dark:border-blue-400 shadow-lg transform scale-105' 
                  : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {t('pricing.recommended', 'Recommended')}
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center mb-2">
                  <span className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
                    {plan.price}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300 ml-1">
                    {plan.period}
                  </span>
                </div>
                {activeTab === 'yearly' && plan.yearlyPrice && (
                  <div className="text-center mb-2">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">
                      {plan.yearlyPrice} {plan.yearlyPeriod}
                    </span>
                  </div>
                )}
                {plan.originalPrice && (
                  <div className="text-center">
                    <span className="text-gray-500 dark:text-gray-400 line-through text-sm">
                      {plan.originalPrice}
                    </span>
                    <span className="ml-2 text-green-600 dark:text-green-400 text-sm font-medium">
                      {t('pricing.save', 'Save')} {plan.name === 'Starter' ? savings.starter : savings.professional}%
                    </span>
                  </div>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300 text-sm">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePlanSelection(plan)}
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-semibold transition-all flex items-center justify-center ${
                  plan.popular
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg'
                    : plan.isCustom
                      ? 'bg-gray-800 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-900 dark:text-white'
                } disabled:opacity-50`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    {plan.buttonText}
                    {!plan.isCustom && <ArrowRight className="w-4 h-4 ml-2" />}
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 md:px-6 pb-4 text-sm text-gray-600 text-center">
        <p>After subscribing, your organization's account will be automatically updated.</p>
      </div>
    </div>
  );
};

export default Payments; 